import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChartColumn,
  faChevronDown,
  faChevronRight,
  faComputerMouse,
  faEye,
  faLightbulb,
  faMagnifyingGlass,
  faRotateRight,
  faTriangleExclamation,
  faUserCheck,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../../services/api';
import './AdminDashboard.css';
import './AdminAnalyticsDashboard.css';

const number = new Intl.NumberFormat('pt-BR');
const percent = (value) => `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const safeList = (value) => Array.isArray(value) ? value : [];

const dateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const eventLabel = {
  page_view: 'Página visualizada',
  search: 'Busca',
  course_click: 'Clique em curso',
  interaction: 'Interação',
};

const sourceLabel = {
  search_dropdown: 'Busca rápida',
  search_results: 'Página de busca',
  home_catalog: 'Catálogo inicial',
  course_catalog: 'Catálogo de cursos',
  course_link: 'Link de curso',
  page_action: 'Ação na página',
  outros: 'Outros',
};

const deviceLabel = {
  mobile: 'Celular',
  tablet: 'Tablet',
  desktop: 'Computador',
  bot: 'Robô de busca',
  unknown: 'Não identificado',
};

const DeviceGroup = ({ title, items, labelKey, labels = {} }) => {
  const rows = safeList(items);
  const maxSessions = Math.max(...rows.map((item) => Number(item.sessions || 0)), 1);

  return (
    <div className="analytics-device-group">
      <h3>{title}</h3>
      {rows.map((item) => {
        const key = item[labelKey];
        return (
          <div className="analytics-device-row" key={key}>
            <div><span>{labels[key] || key}</span><strong>{number.format(item.sessions)} sessões</strong></div>
            <i><span style={{ width: `${(item.sessions / maxSessions) * 100}%` }} /></i>
          </div>
        );
      })}
      {rows.length === 0 && <p>Sem dados no período.</p>}
    </div>
  );
};

const AnalyticsMetric = ({ icon, label, value, type }) => (
  <article className="admin-metric-card analytics-metric-card">
    <div className="admin-metric-icon"><FontAwesomeIcon icon={icon} /></div>
    <div>
      <span className="admin-metric-label">{label}</span>
      <strong>{type === 'percent' ? percent(value) : number.format(value || 0)}</strong>
    </div>
  </article>
);

const TimelineChart = ({ items }) => {
  const rows = safeList(items);
  const maxValue = Math.max(...rows.flatMap((item) => [
    Number(item.page_views || 0),
    Number(item.searches || 0),
    Number(item.course_clicks || 0),
  ]), 1);

  return (
    <div className="analytics-chart-scroll">
      <div className="analytics-timeline" style={{ '--analytics-days': Math.max(rows.length, 7) }}>
        {rows.map((item) => (
          <div className="analytics-day" key={item.date} title={`${item.label}: ${item.page_views} páginas, ${item.searches} buscas, ${item.course_clicks} cursos`}>
            <div className="analytics-day-bars">
              <span className="views" style={{ height: `${Math.max((item.page_views / maxValue) * 100, item.page_views ? 3 : 0)}%` }} />
              <span className="searches" style={{ height: `${Math.max((item.searches / maxValue) * 100, item.searches ? 3 : 0)}%` }} />
              <span className="courses" style={{ height: `${Math.max((item.course_clicks / maxValue) * 100, item.course_clicks ? 3 : 0)}%` }} />
            </div>
            <small>{item.label}</small>
          </div>
        ))}
      </div>
    </div>
  );
};

const Heatmap = ({ points }) => {
  const rows = safeList(points);
  const maxClicks = Math.max(...rows.map((point) => Number(point.clicks || 0)), 1);

  return (
    <div className="analytics-heatmap" aria-label="Mapa de calor dos cliques na página selecionada">
      <div className="analytics-browser-bar"><span /><span /><span /></div>
      <div className="analytics-heatmap-surface">
        {rows.map((point, index) => {
          const intensity = Number(point.clicks || 0) / maxClicks;
          return (
            <span
              className="analytics-heat-point"
              key={`${point.x}-${point.y}-${index}`}
              style={{
                left: `${point.x}%`,
                top: `${point.y}%`,
                opacity: 0.35 + (intensity * 0.65),
                transform: `translate(-50%, -50%) scale(${0.65 + intensity})`,
              }}
              title={`${number.format(point.clicks)} clique(s)`}
            />
          );
        })}
        {rows.length === 0 && <div className="analytics-heatmap-empty">Os pontos aparecerão conforme os usuários interagirem com esta página.</div>}
      </div>
    </div>
  );
};

const AdminAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [selectedPage, setSelectedPage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedActivityGroups, setExpandedActivityGroups] = useState([]);

  const loadAnalytics = useCallback(async (page = '') => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.admin.analytics(days, page);
      setAnalytics(response.data);
      setSelectedPage(response.data?.heatmap?.selected_page || '');
    } catch {
      setError('Não foi possível carregar os dados de comportamento.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadAnalytics('');
  }, [loadAnalytics]);

  const metrics = analytics?.metrics || {};
  const recentActivityGroups = safeList(analytics?.recent_activity_groups);
  const topCourses = safeList(analytics?.top_courses);
  const maxCourseClicks = Math.max(...topCourses.map((course) => Number(course.clicks || 0)), 1);
  const heatmap = analytics?.heatmap || {};
  const metricItems = [
    { label: 'Sessões', value: metrics.sessions, icon: faUsers },
    { label: 'Usuários identificados', value: metrics.identified_users, icon: faUserCheck },
    { label: 'Páginas visualizadas', value: metrics.page_views, icon: faEye },
    { label: 'Buscas realizadas', value: metrics.searches, icon: faMagnifyingGlass },
    { label: 'Cliques em cursos', value: metrics.course_clicks, icon: faComputerMouse },
    { label: 'Busca para clique', value: metrics.search_click_rate, icon: faChartColumn, type: 'percent' },
  ];

  const toggleActivityGroup = (actorKey) => {
    setExpandedActivityGroups((current) => (
      current.includes(actorKey)
        ? current.filter((key) => key !== actorKey)
        : [...current, actorKey]
    ));
  };

  return (
    <main className="admin-dashboard-page">
      <div className="admin-dashboard-shell">
        <header className="admin-dashboard-header analytics-header">
          <div>
            <span className="admin-eyebrow">Inteligência de navegação</span>
            <h1>Comportamento e buscas</h1>
            <p>Interesse dos usuários, descoberta de cursos e distribuição dos cliques nas páginas.</p>
          </div>
          <div className="admin-header-actions">
            <Link className="admin-nav-button" to="/admin/percepcao-de-uso" title="Abrir jornada consolidada">
              <FontAwesomeIcon icon={faLightbulb} />
              Percepção de uso
            </Link>
            <Link className="admin-nav-button" to="/admin" title="Voltar ao painel operacional">
              <FontAwesomeIcon icon={faArrowLeft} />
              Painel geral
            </Link>
            <button className="admin-icon-button" onClick={() => loadAnalytics(selectedPage)} type="button" title="Atualizar dados">
              <FontAwesomeIcon icon={faRotateRight} spin={loading} />
            </button>
          </div>
        </header>

        <div className="analytics-toolbar" aria-label="Período analisado">
          <span>Período</span>
          <div className="analytics-period-control">
            {[7, 30, 90].map((period) => (
              <button type="button" key={period} className={days === period ? 'active' : ''} onClick={() => setDays(period)}>
                {period} dias
              </button>
            ))}
          </div>
          <small>Atualizado em {dateTime(analytics?.generated_at)}</small>
        </div>

        {error && (
          <div className="admin-alert">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{error}</span>
          </div>
        )}

        {loading && !analytics ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faRotateRight} spin />
            <span>Carregando comportamento dos usuários...</span>
          </div>
        ) : (
          <>
            <section className="admin-metrics-grid analytics-metrics-grid" aria-label="Indicadores de comportamento">
              {metricItems.map((item) => <AnalyticsMetric key={item.label} {...item} />)}
            </section>

            <section className="admin-section analytics-section-wide">
              <div className="admin-section-title">
                <h2>Atividade no período</h2>
                <div className="analytics-legend">
                  <span className="views">Páginas</span>
                  <span className="searches">Buscas</span>
                  <span className="courses">Cursos</span>
                </div>
              </div>
              <TimelineChart items={analytics?.timeline} />
            </section>

            <div className="admin-grid-two analytics-main-grid">
              <section className="admin-section">
                <div className="admin-section-title">
                  <h2>Termos mais buscados</h2>
                  <span>{number.format(metrics.zero_result_searches || 0)} sem resultado</span>
                </div>
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead><tr><th>Busca</th><th>Vezes</th><th>Resultados</th><th>Usuários</th></tr></thead>
                    <tbody>
                      {safeList(analytics?.top_searches).map((item) => (
                        <tr key={item.query}>
                          <td><strong>{item.query}</strong>{Number(item.zero_results) > 0 && <small>{item.zero_results} sem resultado</small>}</td>
                          <td>{number.format(item.searches)}</td>
                          <td>{Number(item.average_results || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}</td>
                          <td>{number.format(item.identified_users)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {safeList(analytics?.top_searches).length === 0 && <div className="admin-empty">As buscas começarão a aparecer após a nova coleta.</div>}
                </div>
              </section>

              <section className="admin-section">
                <div className="admin-section-title"><h2>Cursos mais clicados</h2></div>
                <div className="analytics-ranking">
                  {topCourses.map((course, index) => (
                    <div className="analytics-ranking-row" key={course.course_id}>
                      <span className="admin-rank">{index + 1}</span>
                      <div>
                        <div><strong>{course.title || `Curso ${course.course_id}`}</strong><span>{number.format(course.clicks)} cliques</span></div>
                        <span className="analytics-ranking-track"><i style={{ width: `${(course.clicks / maxCourseClicks) * 100}%` }} /></span>
                        <small>{number.format(course.sessions)} sessões · {number.format(course.identified_users)} usuários identificados</small>
                      </div>
                    </div>
                  ))}
                  {topCourses.length === 0 && <div className="admin-empty">Os cliques em cursos começarão a aparecer após a nova coleta.</div>}
                </div>
              </section>
            </div>

            <section className="admin-section analytics-section-wide">
              <div className="admin-section-title"><h2>Dispositivos de acesso</h2><span>Contagem por sessões</span></div>
              <div className="analytics-device-grid">
                <DeviceGroup title="Tipo de dispositivo" items={analytics?.devices?.types} labelKey="device_type" labels={deviceLabel} />
                <DeviceGroup title="Navegadores" items={analytics?.devices?.browsers} labelKey="browser_name" labels={{ unknown: 'Não identificado' }} />
                <DeviceGroup title="Sistemas operacionais" items={analytics?.devices?.operating_systems} labelKey="operating_system" labels={{ unknown: 'Não identificado' }} />
              </div>
            </section>

            <section className="admin-section analytics-section-wide">
              <div className="admin-section-title analytics-heatmap-title">
                <div><h2>Mapa de calor por página</h2><small>Posição relativa dos cliques em diferentes tamanhos de tela.</small></div>
                <select value={selectedPage} onChange={(event) => { setSelectedPage(event.target.value); loadAnalytics(event.target.value); }} aria-label="Página do mapa de calor">
                  {safeList(heatmap.pages).map((page) => <option key={page.page_path} value={page.page_path}>{page.page_path} ({page.clicks})</option>)}
                  {safeList(heatmap.pages).length === 0 && <option value="">Sem páginas coletadas</option>}
                </select>
              </div>
              <div className="analytics-heatmap-layout">
                <Heatmap points={heatmap.points} />
                <div className="analytics-elements">
                  <h3>Elementos mais acionados</h3>
                  {safeList(heatmap.elements).map((item, index) => (
                    <div key={`${item.element_label}-${index}`}><span>{item.element_label}</span><strong>{number.format(item.clicks)}</strong></div>
                  ))}
                  {safeList(heatmap.elements).length === 0 && <p>Sem interações nesta página.</p>}
                </div>
              </div>
            </section>

            <div className="admin-grid-two analytics-main-grid">
              <section className="admin-section">
                <div className="admin-section-title"><h2>Páginas mais visitadas</h2></div>
                <div className="admin-list">
                  {safeList(analytics?.top_pages).map((page, index) => (
                    <div className="admin-list-row" key={page.page_path}>
                      <span className="admin-rank">{index + 1}</span>
                      <span className="admin-list-title">{page.page_path}<small>{number.format(page.clicks)} interações</small></span>
                      <strong>{number.format(page.views)} visitas</strong>
                    </div>
                  ))}
                  {safeList(analytics?.top_pages).length === 0 && <div className="admin-empty">Sem páginas no período.</div>}
                </div>
              </section>

              <section className="admin-section">
                <div className="admin-section-title"><h2>Origem dos cliques em cursos</h2></div>
                <div className="admin-payment-summary analytics-source-grid">
                  {safeList(analytics?.sources).map((item) => (
                    <div key={item.source}><span>{sourceLabel[item.source] || item.source}</span><strong>{number.format(item.clicks)}</strong></div>
                  ))}
                  {safeList(analytics?.sources).length === 0 && <div className="admin-empty">Sem cliques no período.</div>}
                </div>
              </section>
            </div>

            <section className="admin-section analytics-section-wide">
              <div className="admin-section-title"><h2>Atividade de usuários identificados</h2><span>Somente usuários logados</span></div>
              <div className="analytics-table-wrap">
                <table className="analytics-table analytics-user-table">
                  <thead><tr><th>Usuário</th><th>Buscas</th><th>Cursos</th><th>Eventos</th><th>Última atividade</th></tr></thead>
                  <tbody>
                    {safeList(analytics?.identified_activity).map((user) => (
                      <tr key={user.id}>
                        <td><strong>{user.name}</strong><small>{user.email}</small></td>
                        <td>{number.format(user.searches)}</td>
                        <td>{number.format(user.course_clicks)}</td>
                        <td>{number.format(user.events)}</td>
                        <td>{dateTime(user.last_activity_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {safeList(analytics?.identified_activity).length === 0 && <div className="admin-empty">Nenhuma atividade identificada no período.</div>}
              </div>
            </section>

            <section className="admin-section analytics-section-wide">
              <div className="admin-section-title"><h2>Eventos recentes</h2><span>Agrupados por usuário ou IP</span></div>
              <div className="analytics-activity-table">
                <div className="analytics-activity-head" aria-hidden="true">
                  <span>Identificação</span>
                  <span>Atividade</span>
                  <span>Acesso mais recente</span>
                  <span />
                </div>
                <div className="analytics-activity-scroll">
                  {recentActivityGroups.map((group) => {
                    const expanded = expandedActivityGroups.includes(group.actor_key);
                    const anonymousLabel = group.ip_address
                      ? `IP ${group.ip_address}`
                      : `Sessão ${group.session_id?.slice(0, 12) || 'não identificada'}`;

                    return (
                      <div className={`analytics-activity-group${expanded ? ' expanded' : ''}`} key={group.actor_key}>
                        <button
                          type="button"
                          className="analytics-activity-summary"
                          onClick={() => toggleActivityGroup(group.actor_key)}
                          aria-expanded={expanded}
                        >
                          <span>
                            <strong>{group.user?.name || anonymousLabel}</strong>
                            <small>{group.user?.email || (group.ip_address ? 'Usuário não logado' : 'IP não identificado')}</small>
                          </span>
                          <span>
                            <strong>{number.format(group.event_count)} eventos</strong>
                            <small>Último em {dateTime(group.latest_at)}</small>
                          </span>
                          <span>
                            <strong className="analytics-device-title">
                              {deviceLabel[group.device_type] || 'Não identificado'}
                              {group.is_bot && <i className="analytics-bot-flag" title="Identificado pelo User-Agent">BOT</i>}
                            </strong>
                            <small>{group.browser_name || 'Navegador não identificado'} · {group.operating_system || 'Sistema não identificado'}</small>
                          </span>
                          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                        </button>
                        {expanded && (
                          <div className="analytics-activity-events">
                            {safeList(group.events).map((event) => (
                              <div className="analytics-activity-event" key={event.id}>
                                <span className={`analytics-event-type ${event.event_type}`}>{eventLabel[event.event_type] || event.event_type}</span>
                                <span>
                                  <strong>{event.search_query || event.course?.title || event.element_label || event.page_path}</strong>
                                  <small>{event.page_path}</small>
                                </span>
                                <span>
                                  <small>IP {event.ip_address || 'não identificado'}</small>
                                  <strong>{dateTime(event.created_at)}</strong>
                                </span>
                              </div>
                            ))}
                            {Number(group.event_count) > safeList(group.events).length && (
                              <div className="analytics-activity-limit">Exibindo os 20 eventos mais recentes deste grupo.</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {recentActivityGroups.length === 0 && <div className="admin-empty">Os eventos aparecerão aqui em tempo quase real.</div>}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default AdminAnalyticsDashboard;
